<script setup>
import { ref, reactive, computed, onMounted } from "vue"
import { Inertia } from '@inertiajs/inertia'
import HandlesAuthorization from "@/Mixins/HandlesAuthorization.js"

import route from 'ziggy'

import {
	CogIcon,
	CreditCardIcon,
	LibraryIcon,
	BookmarkAltIcon,
	DocumentTextIcon,
	UserCircleIcon,
	UserGroupIcon,
	HomeIcon,
	LogoutIcon,
	TicketIcon,
	SwitchHorizontalIcon,
	InformationCircleIcon,
	ClipboardCheckIcon 
} from '@heroicons/vue/outline'

import { useAuthStore } from '@/Stores/auth'
const auth = useAuthStore()

const usersFirstOrganisationId = computed(() => {
	return auth.user.organisations && auth.user.organisations.length > 0 ?
		auth.user.organisations[0].id :
		0
})

const navigation = computed(() => {
	return [
		{
			name: 'Home',
			href: route('dashboard.home'),
			icon: HomeIcon,
			current: route().current() === 'dashboard.home',
			condition: () => true,
			testid: 'nav-link-home-link',
		},
		{
			name: 'Events',
			href: route('dashboard.events.index'),
			icon: TicketIcon,
			current: route().current() === 'dashboard.events.index',
			condition: () => true,
			testid: 'nav-events-link',
		},
		{
			name: 'Organisations',
			href: route('dashboard.organisations.index'),
			icon: LibraryIcon,
			current: route().current() === 'dashboard.organisations.index',
			condition: () => auth.isAdmin || auth.user.organisations?.length > 1,
			testid: 'nav-organisations-link',
		},
		{
			name: 'My Organisation',
			href: route('dashboard.organisations.show', usersFirstOrganisationId.value),
			icon: LibraryIcon,
			current: route().current() === 'dashboard.organisations.show',
			condition: () => auth.user.organisations?.length == 1,
			testid: 'nav-my-organisation-link',
		},
		{
			name: 'Contacts',
			href: route('dashboard.contacts.index'),
			icon: UserGroupIcon,
			current: route().current() === 'dashboard.contacts.index',
			condition: () => !auth.isAdmin && auth.can('manage_contacts'),
			testid: 'nav-contacts-link',
		},
		{
			name: 'Users',
			href: route('dashboard.users.index'),
			icon: UserGroupIcon,
			current: route().current() === 'dashboard.users.index',
			condition: () => auth.isAdmin,
			testid: 'nav-users-link',
		},
		{
			name: 'Invoices',
			href: route('dashboard.invoices.index'),
			icon: BookmarkAltIcon,
			current: route().current() === 'dashboard.invoices.index',
			condition: () => auth.isAdmin || auth.can('view_invoices'),
			testid: 'nav-invoices-link',
		},
		{
			name: 'Payment Methods',
			href: route('dashboard.cards.index'),
			icon: CreditCardIcon,
			current: route().current() === 'dashboard.cards.index',
			condition: () => !auth.isAdmin && auth.can('view_invoices'),
			testid: 'nav-cards-link',
		},
		// {
		// 	name: 'Server Logs',
		// 	href: route('dashboard.logs.index'),
		// 	icon: DocumentTextIcon,
		// 	current: route().current() === 'dashboard.logs.index',
		// 	condition: () => auth.isAdmin,
		// 	testid: 'nav-logs-link',
		// },
		{
			name: 'Membership Applications',
			href: route('dashboard.membership-applications.index'),
			icon: ClipboardCheckIcon,
			current: route().current() === 'dashboard.membership-applications.index',
			condition: () => auth.isAdmin,
			testid: 'nav-membership-applications-link',
		},
		{
			name: 'Activity Log',
			href: route('dashboard.activity-logs.index'),
			icon: SwitchHorizontalIcon,
			current: route().current() === 'dashboard.activity-logs.index',
			condition: () => auth.isAdmin,
			testid: 'nav-activity-logs-link',
		},
		{
			name: 'My Account',
			href: route('dashboard.users.show', auth.id),
			icon: UserCircleIcon,
			current: route().current() === 'dashboard.users.show' && route().params.user == auth.id,
			condition: () => true,
			testid: 'nav-my-account-link',
		},
		{
			name: 'Documentation',
			href: route('dashboard.docs.home'),
			icon: InformationCircleIcon,
			current: route().current() === 'dashboard.docs.home',
			condition: () => true,
			testid: 'nav-docs-link',
		},
		{
			name: 'Settings',
			href: route('dashboard.settings'),
			icon: CogIcon,
			current: route().current() === 'dashboard.settings',
			condition: () => auth.isAdmin,
			testid: 'nav-settings-link',
		},
	]
})

function addMemberOrganisation() {
	Inertia.visit('/organisations/create', {method: 'get'})
}

const { logout } = HandlesAuthorization()
</script>

<template>

	<nav class="space-y-1">
		<div 
			v-for="item in navigation"
			:key="item.name"
		>
			<Link
				v-if="item.condition()" 
				:href="item.href" 
				:class="[
					item.current ? 'bg-gray-50 text-orange-600 hover:bg-white' : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50',
					'group rounded-md p-6 md:px-3 md:py-2 flex items-center text-sm font-medium'
				]" 
				:aria-current="item.current ? 'page' : undefined"
				:data-testid="item.testid"
			>
				<component 
					:is="item.icon" 
					:class="[item.current ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500', 'flex-shrink-0 -ml-1 mr-3 h-6 w-6']" 
					aria-hidden="true" 
				/>
				<span class="truncate">
					{{ item.name }}
				</span>
			</Link>
		</div>
		
		<Link 
			href="#"
			class="text-gray-900 hover:text-gray-900 hover:bg-gray-50 group rounded-md p-6 md:px-3 md:py-2 flex items-center text-sm font-medium"
			data-testid="logout-button"
			@click="logout()"
		>
			<LogoutIcon
				class="text-gray-400 group-hover:text-gray-500 flex-shrink-0 -ml-1 mr-3 h-6 w-6" 
			/>
			<span class="truncate">
				Log out
			</span>
		</Link>
	</nav>

</template>